import { useRouter } from 'next/router';
import { useContext, useMemo, useState } from 'react';
import useSWR from 'swr';
import useSchoolTournament from './useSchoolTournament';
import { ROLE_VALUE } from '@/constants';
import { COURT_RENTAL_PERMISSION, SCHOOL_PERMISSION, TOURNAMENT_PERMISSON } from '@/constants/permission';
import SchoolContext from '@/contexts/school';
import { fetcher } from '@/plugins/graphql';
import type { User } from '@/types/User';

const useCurrentUser = (uid: string | undefined) => {
  const { schema, is_school_contract, is_tournament_contract, is_rental_contract, id } = useContext(SchoolContext);

  const router = useRouter();

  const [{ schoolTournament }] = useSchoolTournament(router.query.tournament_id, id);

  const routerName = router.pathname;

  const [permission, setPermission] = useState<string[]>([]);

  const query = useMemo(() => {
    return `
  {
    ${schema}_user(where: {
      deleted_at: {
        _is_null: true
      },
      uuid: {
        _eq: "${uid}"
      }
    }) {
      uuid
      name
      id
      school_role
      tournament_role
      court_rental_role
      email
      tournament_permissions {
        school_tournament_id
      }
    }
  }
`;
  }, [schema, uid]);

  const { data, error, mutate } = useSWR(schema && uid ? query : null, fetcher, {
    revalidateOnFocus: false,
  });

  const user: User | null | undefined = useMemo(() => {
    const user = data?.[`${schema}_user`]?.[0];
    if (user) {
      // Get tournament permission
      const tournamentPermissionsIds = user.tournament_permissions.map(
        (permission: any) => permission.school_tournament_id,
      );

      user.tournamentIds = tournamentPermissionsIds;

      const schoolRole = is_school_contract ? user.school_role || 0 : 0;

      const tournamentRole = is_tournament_contract ? user.tournament_role || 0 : 0;

      const court_rental_role = is_rental_contract ? user.court_rental_role || 0 : 0;

      // Get permission
      const schoolPermission = SCHOOL_PERMISSION[schoolRole] || [];

      // Get tournament permission
      const tournamentPermission = TOURNAMENT_PERMISSON[tournamentRole] || [];

      // Get court rental permission
      const courtRentalPermission = COURT_RENTAL_PERMISSION[court_rental_role] || [];

      const _permission = [...schoolPermission, ...tournamentPermission, ...courtRentalPermission];

      setPermission(_permission);

      if (_permission.includes(routerName)) {
        if (tournamentRole !== ROLE_VALUE.PARTNER) {
          user.canAccess = true;
        } else {
          // Check tournament permission
          const tournamentId = schoolTournament?.amateur_tournament_id
            ? schoolTournament?.amateur_tournament_id
            : schoolTournament?.id;

          if (tournamentId && !tournamentPermissionsIds.includes(tournamentId)) {
            user.canAccess = false;
          } else {
            user.canAccess = true;
          }
        }
      } else {
        user.canAccess = false;
      }
      return user;
    } else {
      return null;
    }
  }, [data, schema, is_school_contract, is_tournament_contract, is_rental_contract, routerName, schoolTournament]);

  /**
   * Check if can access to router
   * @param routerName
   */
  const checkAccess = (routerName: string): boolean => {
    // Check permisison
    if (permission.includes(routerName)) {
      return true;
    } else {
      return false;
    }
  };

  const userExists = useMemo(() => {
    return !!user || false;
  }, [user]);

  return {
    data: data
      ? {
          user,
          userExists,
        }
      : undefined,
    error,
    mutate,
    checkAccess,
  };
};

export default useCurrentUser;
